import { useEffect, useState } from "react";
import { Coins, Loader2, X, Check } from "lucide-react";
import { api, formatErr } from "../lib/api";
import { useFx } from "../lib/fx";
import { useI18n } from "../lib/i18n";

/**
 * Modal for redeeming MINT points against a booking deposit.
 *
 * Flow:
 *  1. Fetch /api/bookings/{id}/mint-quote?points=0 to get available + max allowed
 *  2. User adjusts the points amount with a slider
 *  3. Re-quote on every change (debounced via input change)
 *  4. Confirm → POST /api/bookings/{id}/apply-mint
 */
export default function MintRedemptionModal({ booking, onClose }) {
  const { t } = useI18n();
  const { format } = useFx();
  const [quote, setQuote] = useState(null);
  const [points, setPoints] = useState(0);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loadingQuote, setLoadingQuote] = useState(true);

  const fetchQuote = async (p) => {
    try {
      const { data } = await api.get(`/bookings/${booking.id}/mint-quote`, {
        params: { points: p },
      });
      setQuote(data);
      return data;
    } catch (e) {
      setError(formatErr(e));
      return null;
    }
  };

  useEffect(() => {
    (async () => {
      setLoadingQuote(true);
      const q = await fetchQuote(0);
      if (q) setPoints(Math.min(q.max_points_allowed, q.available_points));
      setLoadingQuote(false);
    })();
  }, [booking.id]);

  // Re-fetch quote when points change
  useEffect(() => {
    if (loadingQuote) return;
    fetchQuote(points);
  }, [points]);

  const submit = async () => {
    if (!points || points < 1) return;
    setBusy(true); setError(null);
    try {
      await api.post(`/bookings/${booking.id}/apply-mint`, { points });
      onClose(true);
    } catch (e) { setError(formatErr(e)); }
    finally { setBusy(false); }
  };

  const max = quote?.max_points_allowed || 0;
  const available = quote?.available_points || 0;
  const cap = quote?.max_percent || 50;
  const canSubmit = quote && points >= 1 && points <= max && points <= available;

  return (
    <div className="fixed inset-0 z-[60] flex items-end md:items-center justify-center p-0 md:p-6 bg-ink-deep/70 backdrop-blur-sm"
         onClick={() => !busy && onClose(false)}>
      <div onClick={(e) => e.stopPropagation()}
           className="w-full md:max-w-md max-h-[95svh] overflow-y-auto modal-panel p-6 space-y-4"
           data-testid="mint-redemption-modal">
        <div className="flex items-start justify-between">
          <div>
            <div className="eyebrow flex items-center gap-2 text-teal">
              <Coins size={12}/> {t("user_dash.mint_redeem_eyebrow", "Mint Points")}
            </div>
            <h3 className="font-serif-display text-2xl mt-1">
              {t("user_dash.mint_redeem_title", "Redeem MINT")}
            </h3>
          </div>
          <button onClick={() => !busy && onClose(false)} className="text-muted hover:text-bone" data-testid="mint-modal-close">
            <X size={20}/>
          </button>
        </div>

        <p className="text-sm text-muted">
          {t("user_dash.mint_redeem_subtitle",
             "Use your earned MINT points to reduce this booking's deposit. Up to {{cap}}% of the deposit can be paid with MINT.",
             { cap })}
        </p>

        {loadingQuote ? (
          <div className="flex items-center gap-2 text-sm text-muted py-6">
            <Loader2 size={14} className="animate-spin"/> {t("common.loading", "Loading…")}
          </div>
        ) : !quote ? (
          <div className="text-rose-300 text-sm">{error || t("common.something_went_wrong", "Something went wrong.")}</div>
        ) : (
          <>
            <div className="luxe-card p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted">{t("user_dash.mint_balance", "Your MINT balance")}</span>
                <span className="font-mono">{available.toLocaleString()} MINT</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted">{t("user_dash.mint_max_allowed", "Max for this booking")}</span>
                <span className="font-mono">{max.toLocaleString()} MINT</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted">{t("user_dash.mint_rate", "Rate")}</span>
                <span className="font-mono">1 MINT = €{(quote.rate_eur || 0).toFixed(2)}</span>
              </div>
            </div>

            {max < 1 ? (
              <div className="luxe-card p-4 text-sm text-rose-200/80 border-rose-500/30" data-testid="mint-no-balance">
                {available < 1
                  ? t("user_dash.mint_no_balance", "You don't have any MINT yet. Earn 1 MINT for every €1 spent on completed bookings.")
                  : t("user_dash.mint_too_small", "This booking is too small for a meaningful MINT redemption.")}
              </div>
            ) : (
              <>
                <label className="block">
                  <div className="flex justify-between items-baseline mb-2">
                    <span className="label-tiny">{t("user_dash.mint_amount", "Points to redeem")}</span>
                    <span className="font-mono text-base text-teal">{points.toLocaleString()} MINT</span>
                  </div>
                  <input type="range"
                         min={0}
                         max={max}
                         step={1}
                         value={points}
                         onChange={(e) => setPoints(Number(e.target.value))}
                         className="w-full accent-teal"
                         data-testid="mint-slider"/>
                  <div className="flex justify-between text-[10px] text-muted/70 font-mono mt-1">
                    <span>0</span>
                    <span>{max.toLocaleString()}</span>
                  </div>
                </label>

                <div className="luxe-card p-4 space-y-1.5 border-teal/40 bg-teal/5">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted">{t("user_dash.mint_discount", "Discount")}</span>
                    <span className="font-serif-display text-teal text-lg" data-testid="mint-discount-amount">
                      −{format(quote.discount_native || 0, quote.currency)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted">{t("user_dash.mint_new_deposit", "New deposit")}</span>
                    <span className="font-serif-display text-lg" data-testid="mint-new-deposit">
                      {format(quote.deposit_after_discount || 0, quote.currency)}
                    </span>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {error && <div className="text-rose-300 text-sm">{error}</div>}

        <div className="flex gap-2 pt-2">
          <button type="button" onClick={() => onClose(false)} disabled={busy} className="btn btn-ghost flex-1" data-testid="mint-cancel">
            {t("common.cancel", "Cancel")}
          </button>
          <button type="button" onClick={submit} disabled={!canSubmit || busy} className="btn btn-primary flex-1" data-testid="mint-confirm">
            {busy ? <Loader2 size={14} className="animate-spin"/> : <Check size={14}/>}
            {t("user_dash.mint_confirm", "Apply MINT")}
          </button>
        </div>
      </div>
    </div>
  );
}
